import React from 'react';
import { Link } from '@mui/material';
import { TripMatch, TripRequest } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import {
  CopyButton,
  DetailCard,
  DetailCardProps,
  formatCountryCodesToString,
  getBudgetPerPersonString,
  getFormattedDateShortMonthDDYYYY,
  getTotalBudgetString,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import ChipList from '../chip-list/trip-match';
import useFetchItineraryPath from '../../hooks/use-fetch-itinerary-path';

type TripMatchCardProps = {
  chips?: string[] | undefined,
  title?: string,
  titleTypographyProps?: DetailCardProps['titleTypographyProps'];
  tripMatch: TripMatch | undefined,
  tripRequest: TripRequest | undefined,
  action?: DetailCardProps['action'];
};

const TripDetailCard = ({
  chips = undefined,
  title = undefined,
  titleTypographyProps = {},
  tripMatch,
  tripRequest,
  action = undefined,
}: TripMatchCardProps) => {
  const apiClient = useApiClient();
  const countryCodes = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const itineraryPath = useFetchItineraryPath(tripRequest?.basedOnItineraryId);

  if (!tripMatch) {
    return null;
  }

  const {
    countries,
    departureDate,
    totalNights,
    createdAt,
    budget,
    numberOfAdults,
    numberOfChildren,
    referenceId,
  } = tripMatch;

  let travelersString = `${numberOfAdults} Adults`;
  if (numberOfChildren > 0) {
    travelersString += `, ${numberOfChildren} Children`;
  }

  const basedOnItineraryContent = itineraryPath ? (
    <Link href={itineraryPath} target="_blank" rel="noopener noreferrer">Link to Itinerary</Link>
  ) : '-';

  if (!countryCodes.data) {
    return null;
  }

  return (
    <DetailCard
      chips={chips ? <ChipList chips={chips} /> : undefined}
      title={title}
      action={action}
      titleTypographyProps={titleTypographyProps}
      items={[
        { title: 'Description', content: tripRequest?.tripDescription },
        { title: 'Countries', content: countryCodes && formatCountryCodesToString(countries, countryCodes.data) },
        { title: 'When', content: `${getFormattedDateShortMonthDDYYYY(departureDate) || ''} (${totalNights} Nights)` },
        { title: 'Budget Per Person', content: `${getBudgetPerPersonString(budget, numberOfAdults, numberOfChildren)}` },
        { title: 'Total Budget', content: `${getTotalBudgetString(budget)}` },
        { title: 'Travelers', content: `${travelersString}` },
        { title: 'Based on Itinerary', content: basedOnItineraryContent },
        { title: 'Matched', content: getFormattedDateShortMonthDDYYYY(createdAt) || '' },
        {
          title: 'Reference ID',
          content: referenceId && (
            <>
              {referenceId}
              <CopyButton textToCopy={referenceId} iconFontSize="small" size="small" />
            </>
          ),
        },
      ]}
    />
  );
};

export default TripDetailCard;
